/* ============================================================
   EcoLunch · Bilingual content for the restructured deck
   Single source of truth — 5 verticals + global sections
   ============================================================ */

/* ---- Vertical IDs (stable across languages) ---- */
export const VERTICAL_IDS = ["resto", "care", "school", "club", "fin"];

/* ---- Color tokens per vertical ---- */
export const V_COLORS = {
  resto:  "#fb923c", // orange — meals
  kiddo:  "#fbbf24", // amber — daycare + camps + clubs (EcoKiddo)
  care:   "#fbbf24", // alias (legacy)
  school: "#22d3ee", // cyan — school fees
  club:   "#bef264", // lime — clubs + city/municipal
  fin:    "#3b82f6", // royal blue — prepaid card (EcoCard) — unique, distinct from sub-ledger pink
};

/* ---- Per-model MCC + Eco sub-brand (bilingual) ----
   Used to differentiate sub-merchants inside a merged tab (EcoKiddo = EcoCare + EcoLoisirs/EcoHobby). */
export const MODEL_META = {
  // EcoLunch — Caterers (MCC 5811)
  R1: { mcc: "5811", eco: { fr: "EcoLunch", en: "EcoLunch" } },
  R2: { mcc: "5811", eco: { fr: "EcoLunch", en: "EcoLunch" } },
  R3: { mcc: "5811", eco: { fr: "EcoLunch", en: "EcoLunch" } },
  R4: { mcc: "5811", eco: { fr: "EcoLunch", en: "EcoLunch" } },
  R5: { mcc: "5811", eco: { fr: "EcoLunch", en: "EcoLunch" } },
  // EcoKiddo — Child Care Services / Daycare (MCC 8351)
  G1: { mcc: "8351", eco: { fr: "EcoKiddo", en: "EcoKiddo" } },
  G2: { mcc: "8351", eco: { fr: "EcoKiddo", en: "EcoKiddo" } },
  G3: { mcc: "8351", eco: { fr: "EcoKiddo", en: "EcoKiddo" } },
  G4: { mcc: "8351", eco: { fr: "EcoKiddo", en: "EcoKiddo" } },
  // EcoCamp — Sporting/Recreational Camps (MCC 7032)
  L1: { mcc: "7032", eco: { fr: "EcoCamp", en: "EcoCamp" } },
  L2: { mcc: "7032", eco: { fr: "EcoCamp", en: "EcoCamp" } },
  L4: { mcc: "7032", eco: { fr: "EcoCamp", en: "EcoCamp" } },
  L5: { mcc: "7032", eco: { fr: "EcoCamp", en: "EcoCamp" } },
  // EcoClub — Membership Clubs · Recreation (MCC 7997)
  L3: { mcc: "7997", eco: { fr: "EcoClub", en: "EcoClub" } },
  // EcoSchool — Schools (MCC 8211)
  S1: { mcc: "8211", eco: { fr: "EcoSchool", en: "EcoSchool" } },
  S2: { mcc: "8211", eco: { fr: "EcoSchool", en: "EcoSchool" } },
  // EcoCard — Prepaid (MCC 6051)
  F1: { mcc: "6051", eco: { fr: "EcoCard", en: "EcoCard" } },
  F2: { mcc: "6051", eco: { fr: "EcoCard", en: "EcoCard" } },
  F3: { mcc: "6051", eco: { fr: "EcoCard", en: "EcoCard" } },
  F4: { mcc: "6051", eco: { fr: "EcoCard", en: "EcoCard" } },
};

/* ---- Universal load-up models (W0..W9) shared FR/EN keys ---- */
const W_KEYS = ["W0", "W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9"];

export const I18N = {
  fr: {
    nav: {
      brand: "EcoLunch",
      hero: "Aperçu",
      roadmap: "Roadmap",
      verticals: "Verticales",
      flow: "Flux global",
      wallet: "Wallet · W0→W9",
      ledger: "Ledger",
      why: "Pourquoi MoR",
    },
    hero: {
      title: "Une infrastructure.\nQuatre verticales.\nUn seul wallet.",
      subtitle:
        "Architecture Merchant of Record unifiée — wallet interne, ledger PostgreSQL et payouts hebdomadaires. Une seule source de vérité pour repas, éducation, garde, sport et services financiers.",
      pills: [
        "Merchant of Record",
        "EcoWallet · closed-loop",
        "Ledger PostgreSQL",
        "Payouts hebdomadaires",
        "Zéro split au pay-in",
      ],
    },
    payoutCycle: {
      tag: "Cycle de payout · accumulation many-to-one",
      title: "Plusieurs entrées de payable → un seul batch sortant",
      subtitle: "Chaque transaction client crée une écriture dans le sous-ledger du fournisseur. Les payouts ne sont JAMAIS liés directement à un paiement client — ils naissent d'un cycle agrégé.",
      payee: "Sous-ledger · Traiteur ABC",
      cycle: "Cycle hebdomadaire · vendredi 8h00",
      railsHeader: "Rail",
      entries: [
        { id: "TX-001", label: "R1 commande 10021 · Parent A",    rail: "Visa",    amt: "+ 42,50 $", kind: "in" },
        { id: "TX-014", label: "R2 institutionnel · CSS-A",       rail: "PAD",     amt: "+ 38,00 $", kind: "in" },
        { id: "TX-022", label: "R5 subventionné · École B",       rail: "PAD",     amt: "+ 28,75 $", kind: "in" },
        { id: "TX-031", label: "R3 camp optionnel · Parent B",    rail: "Interac", amt: "+ 15,00 $", kind: "in" },
        { id: "ADJ-01", label: "Remboursement TX-018",            rail: "—",       amt: "− 12,00 $", kind: "out" },
        { id: "ADJ-02", label: "Réserve cyclique",                rail: "—",       amt: "− 10,00 $", kind: "out" },
      ],
      net: "Net du batch",
      netAmt: "= 102,25 $",
      batchId: "BATCH-2026-24",
      newTx: "Nouvelle transaction sortante",
      target: "Traiteur ABC",
      legend: "Plusieurs paiements clients ≠ plusieurs payouts. La sortie est UNE instruction agrégée, distincte du pay-in originel.",
      feeNote: "Les frais de rail (Visa, Mastercard, Interac, PAD, Sezzle, etc.) sont prélevés AU PAY-IN selon le mode utilisé pour CHAQUE transaction. Les montants ci-dessus sont déjà nets du frais de leur rail respectif — aucun frais % global n'est appliqué sur le GMV du cycle.",
    },
    megaFlow: {
      navLabel: "Architecture MoR",
      tag: "Vue d'ensemble · multi-vertical · many-to-one",
      title: "L'architecture complète · 5 verticales convergent dans 1 seul MoR",
      subtitle: "Aucun split au pay-in. Chaque transaction client passe par le même chemin convergent jusqu'au ledger central. Les payouts naissent ailleurs, plus tard, dans un cycle agrégé.",
      columns: {
        sources: "5 sources de pay-in",
        mid: "1 MID unique",
        ledger: "1 ledger central",
        adjust: "Ajustements",
        sub: "Sous-ledgers",
        batch: "Cycle de batching",
        out: "Payout sortant",
      },
      sources: [
        { id: "ec-lunch",  label: "EcoLunch",  desc: "Parents · repas",          color: "#fb923c", phase: "Phase 1" },
        { id: "ec-kiddo",  label: "EcoKiddo",  desc: "Parents · garde · camps",  color: "#fbbf24", phase: "Phase 2" },
        { id: "ec-school", label: "EcoSchool", desc: "Parents · frais scolaires", color: "#22d3ee", phase: "Phase 3" },
        { id: "ec-card",   label: "EcoCard",   desc: "Titulaires · prépayé",     color: "#3b82f6", phase: "Phase 4" },
        { id: "ec-city",   label: "EcoCity",   desc: "Citoyens · municipal",     color: "#10b981", phase: "Phase 5" },
      ],
      mid: { label: "MID EcoTech", desc: "Un seul Merchant ID · toutes les verticales" },
      ledger: { label: "Ledger EcoLunch", desc: "Source unique de vérité · PostgreSQL" },
      adjustments: [
        "Remboursements", "Annulations", "Crédits clients", "Frais contractuels", "Réserves cycliques", "Chargebacks",
      ],
      subledgers: [
        { id: "sl-trait",  label: "Traiteur · sous-ledger" },
        { id: "sl-gard",   label: "Garderie · sous-ledger" },
        { id: "sl-camp",   label: "Camp / Club · sous-ledger" },
        { id: "sl-school", label: "École / CSS · sous-ledger" },
        { id: "sl-city",   label: "Ville / Municipal · sous-ledger" },
      ],
      batch: { label: "Cycle périodique", desc: "Hebdomadaire ou mensuel" },
      payout: { label: "Payout net", desc: "1 instruction sortante par cycle · par bénéficiaire" },
      keyFacts: [
        "Toutes les verticales convergent vers UN seul MID (EcoTech). Aucun split au pay-in.",
        "Le pay-in client et le payout fournisseur sont DEUX transactions financières distinctes.",
        "Le ledger central est la seule source de vérité — il calcule le payable contractuel après livraison.",
        "Les payouts sortants sont N→1 : plusieurs entrées de sous-ledger agrégées en UNE instruction de paiement.",
        "EcoCash / EcoCrédits restent dans l'écosystème — pas de mouvement bancaire externe.",
      ],
    },
    commonRule: {
      tag: "Règle transactionnelle commune",
      body: "Le payeur paie EcoTechSystem inc. — DBA EcoLunch. Le pay-in est traité sous le MID d'EcoTech et les fonds sont réglés à EcoTech. Le ledger EcoLunch enregistre chaque transaction, applique les règles contractuelles et détermine les montants payables. Les payouts aux traiteurs, écoles, CSS, garderies, camps ou clubs sont distincts du paiement initial du client.",
      sequenceTag: "Architecture financière uniforme",
      sequenceGroups: [
        {
          tag: "Transaction client",
          steps: [
            "Paiement du client à EcoTech / EcoLunch",
            "Pay-in traité sous le MID d'EcoTech",
            "100 % des fonds réglés à EcoTech",
            "EcoWallet alimenté du montant exact requis, ou solde EcoWallet existant utilisé",
            "EcoWallet débité pour l'achat",
            "Transaction client, source de fonds et allocation du service inscrites au ledger EcoTech",
            "Allocation au fournisseur inscrite comme conditionnelle et non payable",
          ],
        },
        {
          tag: "Payable contractuel",
          steps: [
            "Service livré ou condition contractuelle applicable confirmée",
            "Le ledger EcoTech calcule et crée le payable net contractuel",
          ],
        },
        {
          tag: "Transaction de payout distincte",
          steps: [
            "EcoTech crée une instruction de payout nouvelle et indépendante",
            "Payout exécuté depuis le compte payout d'EcoTech via le rail de payout applicable",
            "Payout livré au bénéficiaire contractuel et réconcilié au ledger EcoTech",
          ],
        },
      ],
    },
    roadmap: {
      title: "Roadmap de déploiement",
      subtitle: "Cinq phases · une seule infrastructure MoR",
      phases: [
        {
          tag: "Phase 1",
          title: "EcoLunch",
          desc: "Repas scolaires, garderie et camp · cohorte initiale de traiteurs",
          color: "#fb923c",
          mccs: ["5811"],
        },
        {
          tag: "Phase 2",
          title: "EcoKiddo / EcoCamp / EcoClub",
          desc: "Garderies · camps · clubs · sports · loisirs · abonnements",
          color: "#fbbf24",
          mccs: ["8351", "7032", "7033", "7997", "7999", "7911"],
        },
        {
          tag: "Phase 3",
          title: "EcoSchool",
          desc: "Effets scolaires · frais de garde · activités · sorties · matériel · frais scolaires",
          color: "#22d3ee",
          mccs: ["8211"],
        },
        {
          tag: "Phase 4",
          title: "EcoCard",
          desc: "Carte virtuelle prépayée Visa ou Mastercard · paiements externes · Apple Pay · Google Pay · Samsung Pay",
          color: "#3b82f6",
          mccs: ["6051"],
        },
        {
          tag: "Phase 5",
          title: "EcoCity",
          desc: "Services municipaux · citoyens · loisirs publics · activités de ville · taxes & permis admissibles",
          color: "#10b981",
          mccs: ["9399"],
        },
      ],
    },
    verticals: {
      title: "Quatre verticales · classées par rôle économique",
      subtitle: "Sélectionnez un onglet pour voir les modèles, les payeurs, les bénéficiaires et le diagramme animé",
      labels: {
        commandeur: "Commandeur",
        payeur: "Payeur",
        beneficiaire: "Bénéficiaire du service",
        etablissement: "Établissement",
        fournisseur: "Fournisseur payable",
        wallet: "Wallet chargé",
        chargement: "Type de chargement",
        payout: "Payout",
        flow: "Flow",
        viewAnimation: "Voir l'animation",
        principle: "Note",
        mccList: "MCC acceptés pour cette verticale",
      },
      tabs: [
        { id: "resto",  emoji: "🍱", tag: "EcoLunch",  short: "EcoLunch",  mcc: "5811", eco: "EcoLunch",  long: "Repas pour enfants · écoles · garderies · camps · clubs",
          mccs: [
            { code: "5811", label: "Eating Places · Caterers" },
          ],
        },
        { id: "kiddo",  emoji: "👶", tag: "EcoKiddo",  short: "EcoKiddo",  mcc: "8351", eco: "EcoKiddo",  long: "Garderies · camps · clubs · sports · loisirs · abonnements",
          mccs: [
            { code: "8351", label: "Child Care Services" },
            { code: "7032", label: "Sporting & Recreational Camps" },
            { code: "7033", label: "Campgrounds (campsite-only)" },
            { code: "7997", label: "Sports / Recreation Clubs" },
            { code: "7999", label: "Recreation Services" },
            { code: "7911", label: "Dance Schools / Studios" },
            { code: "9399", label: "Municipal Services" },
          ],
        },
        { id: "school", emoji: "🎓", tag: "EcoSchool", short: "EcoSchool", mcc: "8211", eco: "EcoSchool", long: "Effets scolaires, frais de service de garde, activités, sorties, matériel pédagogique, frais administratifs et autres frais scolaires payés par les parents.",
          mccs: [
            { code: "8211", label: "Schools · Elementary & Secondary" },
          ],
        },
        { id: "fin",    emoji: "💳", tag: "EcoCard",   short: "EcoCard",   mcc: "6051", eco: "EcoCard",   long: "Carte virtuelle prépayée Visa ou Mastercard liée à un solde EcoWallet admissible, utilisable auprès de commerçants externes et compatible avec Apple Pay, Google Pay et Samsung Pay (Phase 4).",
          mccs: [
            { code: "6051", label: "Quasi-Cash · Prepaid Card Load" },
          ],
        },
      ],
      resto: {
        intro: "EcoLunch centralise les commandes et les paiements de repas destinés aux enfants. Selon le scénario, le parent ou l'institution commande et paie les repas. Le traiteur prépare et livre les repas, puis reçoit le montant contractuellement payable selon les données du ledger EcoLunch.",
        models: [
          {
            code: "R1",
            title: "Repas scolaires · le parent commande et paie pour son enfant",
            roles: {
              commandeur: "Parent",
              payeur: "Parent ou coparents",
              beneficiaire: "Enfant",
              etablissement: "École",
              fournisseur: "Traiteur",
              wallet: "Wallet familial EcoLunch",
              chargement: "Montant exact à la commande · solde · EcoCrédits",
              payout: "Traiteur (après livraison + réconciliation)",
            },
            flow: "Parent sélectionne les repas de son enfant → Paiement à EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Transaction inscrite au ledger EcoLunch → Repas livré → Livraison confirmée → Payable net du traiteur calculé → Entrée ajoutée au sous-ledger du traiteur",
            principle: "Le ledger conserve deux états : (1) fonds reçus mais non payables tant que la livraison n'est pas confirmée, (2) payable net calculé après confirmation (prix contractuel − annulations · remboursements · chargebacks · réserves · frais). L'école fournit uniquement le contexte opérationnel.",
          },
          {
            code: "R2",
            title: "Commande institutionnelle · commandeur et payeur distincts",
            roles: {
              commandeur: "Gestionnaire interne (école · garderie · camp)",
              payeur: "Compte institutionnel (PAD débité automatiquement)",
              beneficiaire: "Enfants désignés",
              etablissement: "École · garderie · camp",
              fournisseur: "Traiteur",
              wallet: "Pay-in B2B → wallet interne EcoLunch",
              chargement: "PAD · débit auto sur compte institutionnel",
              payout: "Traiteur (après livraison + réconciliation)",
            },
            flow: "Institution crée la commande → Payeur institutionnel désigné paie EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Transaction inscrite au ledger EcoLunch → Repas livrés → Livraison confirmée → Payable net du traiteur calculé → Entrée ajoutée au sous-ledger du traiteur",
            principle: "Le parent n'est PAS dans cet écosystème. Le commandeur n'est pas le payeur — le débit est automatique sur le compte institutionnel.",
          },
          {
            code: "R3",
            title: "Camp ou club · choix entre lunch apporté et repas du traiteur",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Camp ou club",
              fournisseur: "Traiteur (uniquement pour repas commandés)",
              wallet: "Wallet familial",
              chargement: "Sélection par dates · montant exact",
              payout: "Traiteur",
            },
            flow: "Camp ou club publie l'option de repas → Parent choisit un lunch apporté ou un repas du traiteur → Lunch apporté : aucune transaction de repas → Repas du traiteur : paiement à EcoLunch → Transaction inscrite au ledger EcoLunch → Repas livré → Livraison confirmée → Payable net du traiteur calculé → Entrée ajoutée au sous-ledger du traiteur",
            principle: "Le parent peut choisir repas du traiteur OU lunch maison selon la journée.",
          },
          {
            code: "R4",
            title: "Camp ou club · repas fournis à tous · modèle institutionnel",
            roles: {
              commandeur: "Camp ou club",
              payeur: "Camp ou payeur contractuellement désigné",
              beneficiaire: "Tous les enfants inscrits",
              etablissement: "Camp ou club",
              fournisseur: "Traiteur",
              wallet: "Wallet institutionnel",
              chargement: "Chargement institutionnel global",
              payout: "Traiteur",
            },
            flow: "Camp ou club confirme le nombre de participants → Commande institutionnelle de repas → Camp ou club paie EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Transaction inscrite au ledger EcoLunch → Repas livrés à l'institution → Livraison confirmée → Payable net du traiteur calculé → Entrée ajoutée au sous-ledger du traiteur",
            principle: "Le camp est payeur actif. Les repas sont inclus dans le tarif d'inscription du camp.",
          },
          {
            code: "R5",
            title: "Repas subventionnés · le parent commande · l'école paie mensuellement",
            roles: {
              commandeur: "Parent (commande au quotidien)",
              payeur: "École ou CSS (facturation mensuelle consolidée)",
              beneficiaire: "Enfants désignés",
              etablissement: "École",
              fournisseur: "Traiteur",
              wallet: "Pay-in B2B école → wallet interne EcoLunch",
              chargement: "Cumul mensuel des commandes parents · facture EcoLunch en fin de mois",
              payout: "Traiteur · ajouté au payout régulier",
            },
            flow: "Parent commande le repas subventionné pour son enfant → Aucun paiement demandé au parent → Repas inscrit au ledger comme subventionné → Repas livré → Livraison confirmée → Montant ajouté à la facturation mensuelle de l'école ou du CSS → École ou CSS paie EcoLunch → Paiement concilié dans le ledger → Payable net du traiteur calculé selon les règles contractuelles → Entrée ajoutée au sous-ledger du traiteur",
            principle: "Les parents commandent sans payer. L'école ou le CSS reçoit une facture mensuelle consolidée et règle EcoLunch. Le traiteur absorbe les frais contractuels.",
          },
        ],
      },
      kiddo: {
        intro: "EcoKiddo centralise les inscriptions, les frais de garde, les camps, les clubs, les activités sportives, les loisirs et les abonnements. Les frais peuvent être payés par un parent, un coparent ou un tiers autorisé. Chaque paiement est reçu par EcoTech, inscrit au ledger EcoLunch et utilisé pour calculer le montant payable à l'organisation concernée.",
        models: [
          {
            code: "G1",
            title: "Garderie · inscription et frais ponctuels",
            roles: {
              commandeur: "Garderie",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Garderie / CPE",
              fournisseur: "Garderie",
              wallet: "Wallet familial",
              chargement: "Pay-in unique à la demande",
              payout: "Garderie",
            },
            flow: "Garderie crée l'inscription ou le frais ponctuel → Parent reçoit la demande dans EcoKiddo → Parent paie EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Paiement inscrit au ledger EcoLunch → Inscription ou frais confirmé → Payable net de la garderie calculé → Entrée ajoutée au sous-ledger de la garderie",
            principle: "Exemples : inscription · dépôt · frais administratif · journée supplémentaire · retard · activité spéciale.",
          },
          {
            code: "G2",
            title: "Garderie · frais de garde récurrents",
            roles: {
              commandeur: "Garderie",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Garderie",
              fournisseur: "Garderie",
              wallet: "Wallet familial",
              chargement: "Auto-load périodique (semaine · 2 sem · mois · custom)",
              payout: "Garderie",
            },
            flow: "Calendrier de garde établi → Facture périodique créée → Paiement automatique autorisé → EcoWallet alimenté au montant requis → Paiement à EcoLunch à l'échéance → Transaction inscrite au ledger EcoLunch → Payable net de la garderie calculé → Entrée ajoutée au sous-ledger de la garderie",
            principle: "Montant établi selon calendrier hebdo · bimensuel · mensuel ou personnalisé.",
          },
          {
            code: "G3",
            title: "Garderie · frais ajustés selon les présences et les absences",
            roles: {
              commandeur: "Garderie (moteur de facturation)",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Garderie",
              fournisseur: "Garderie",
              wallet: "Wallet familial",
              chargement: "Montant final après validation des présences",
              payout: "Garderie",
            },
            flow: "Présences et absences validées → Montant final calculé → Facture ajustée → Parent paie EcoLunch → Transaction inscrite au ledger EcoLunch → Payable net de la garderie calculé → Entrée ajoutée au sous-ledger de la garderie",
            principle: "Le moteur détermine si une absence est payable, créditable, remboursable ou exclue de la prochaine facture.",
          },
          {
            code: "G4",
            title: "Garderie · frais répartis entre plusieurs payeurs",
            roles: {
              commandeur: "Garderie",
              payeur: "Coparent ou tiers payeur",
              beneficiaire: "Enfant",
              etablissement: "Garderie",
              fournisseur: "Garderie",
              wallet: "Wallet du payeur désigné (split possible)",
              chargement: "Répartition entre payeurs · chargement parallèle",
              payout: "Garderie (consolidé)",
            },
            flow: "Frais de garde attribués → Répartition entre les payeurs autorisés → Chaque payeur paie sa portion à EcoLunch → Paiements consolidés dans le ledger EcoLunch → Montant total payé validé → Payable net de la garderie calculé → Entrée ajoutée au sous-ledger de la garderie",
            principle: "Cas typique : coparentalité 50/50 ou tiers payeur (grand-parent, sponsor, employeur).",
          },
          {
            code: "L1",
            title: "Camp ou club · inscription payée en totalité",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Wallet familial",
              chargement: "Pay-in unique à l'inscription",
              payout: "Camp / club",
            },
            flow: "Camp ou club publie l'activité → Parent inscrit son enfant → Parent paie la totalité à EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Paiement inscrit au ledger EcoLunch → Inscription confirmée → Payable net du camp ou du club calculé → Entrée ajoutée au sous-ledger du camp ou du club",
            principle: "Modèle le plus simple : un seul paiement à l'inscription.",
          },
          {
            code: "L2",
            title: "Camp ou club · inscription payée en versements",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Wallet familial",
              chargement: "Dépôt initial · puis auto-loads programmés jusqu'au solde",
              payout: "Camp / club (échelonné ou consolidé)",
            },
            flow: "Inscription confirmée → Dépôt initial payé à EcoLunch → Échéancier activé → Versements payés à EcoLunch aux dates prévues → Chaque paiement est inscrit au ledger EcoLunch → Payable net du camp ou du club calculé selon l'échéancier contractuel → Entrée ajoutée au sous-ledger du camp ou du club",
            principle: "Montant total connu et fini (ex. : 1 200 $ en 4 versements). Ledger garde l'échéancier · solde restant · statut de chaque versement. À distinguer de L3 (abonnement ouvert sans terminus).",
          },
          {
            code: "L3",
            title: "Club · abonnement et frais récurrents",
            roles: {
              commandeur: "Club",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Club",
              fournisseur: "Club",
              wallet: "Wallet familial",
              chargement: "Auto-load récurrent indéfini (mensuel · trimestriel · annuel)",
              payout: "Club",
            },
            flow: "Parent souscrit à l'abonnement → Calendrier de facturation activé → Paiement récurrent à EcoLunch à chaque échéance → Transaction inscrite au ledger EcoLunch → Payable net du club calculé → Entrée ajoutée au sous-ledger du club",
            principle: "Abonnement type SaaS · montant non plafonné · récurrence indéfinie. À distinguer de L2 (inscription à montant fini avec échéancier).",
          },
          {
            code: "L4",
            title: "Camp ou club · options et frais additionnels",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Wallet familial",
              chargement: "Facture complémentaire ponctuelle",
              payout: "Camp / club",
            },
            flow: "Parent ajoute une option ou un service → Frais additionnel calculé → Parent paie EcoLunch → Paiement inscrit au ledger EcoLunch → Option confirmée → Payable net du camp ou du club calculé → Entrée ajoutée au sous-ledger du camp ou du club",
            principle: "Exemples : sorties · transport · équipement · garde prolongée · activités spéciales.",
          },
          {
            code: "L5",
            title: "Camp · inscription et repas du traiteur dans un même panier",
            roles: {
              commandeur: "Parent (inscription) + Camp (repas)",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "Camp",
              fournisseur: "Camp (inscription) + Traiteur (repas)",
              wallet: "Wallet familial",
              chargement: "Paiement unique multi-vertical",
              payout: "Camp ET Traiteur (séparés dans le ledger)",
            },
            flow: "Parent sélectionne l'inscription et les repas → Paiement unique à EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Ledger EcoLunch sépare la portion inscription et la portion repas → Portion inscription : payable net du camp calculé selon les règles d'inscription → Portion repas : montant non payable avant la livraison → Repas livré → Livraison confirmée → Entrée payable du traiteur calculée → Entrées ajoutées aux sous-ledgers du camp ET du traiteur (séparés)",
            principle: "Aucun split au PSP. La séparation est strictement effectuée dans le ledger EcoLunch.",
          },
        ],
      },
      school: {
        intro: "Les écoles et les CSS créent les frais scolaires dans EcoSchool. Les parents consultent et paient ces frais dans la section « Frais scolaires » de l'application EcoLunch. Les paiements sont reçus par EcoTech, inscrits au ledger EcoLunch et donnent lieu à un montant payable à l'école ou au CSS selon les règles applicables.",
        models: [
          {
            code: "S1",
            title: "Frais scolaires · paiement unique par le parent",
            roles: {
              commandeur: "École ou CSS (facturier)",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "École / CSS",
              fournisseur: "École, CSS ou organisme désigné",
              wallet: "Wallet familial",
              chargement: "Demande de paiement · pay-in unique",
              payout: "École / CSS / organisme désigné",
            },
            flow: "École ou CSS crée le frais → Parent reçoit la demande dans l'application EcoLunch → Parent paie EcoLunch → Pay-in sous le MID EcoTech → Fonds réglés à EcoTech → Paiement inscrit au ledger EcoLunch → Payable net de l'école ou du CSS calculé selon les règles du frais → Entrée ajoutée au sous-ledger de l'école ou du CSS",
            principle: "Exemples : activités, sorties, matériel, frais administratifs ou services scolaires.",
          },
          {
            code: "S2",
            title: "Frais scolaires · paiement en versements planifiés",
            roles: {
              commandeur: "École (créateur de l'échéancier)",
              payeur: "Parent",
              beneficiaire: "Enfant",
              etablissement: "École / CSS",
              fournisseur: "École / CSS",
              wallet: "Wallet familial",
              chargement: "Auto-load programmé · à chaque échéance",
              payout: "Établissement",
            },
            flow: "École ou CSS crée le frais et l'échéancier → Parent autorise les versements → EcoWallet alimenté au montant requis à chaque échéance → Versement payé à EcoLunch → Paiement inscrit au ledger EcoLunch → Payable net de l'école ou du CSS calculé pour chaque montant payé → Entrée ajoutée au sous-ledger de l'école ou du CSS",
            principle: "Le ledger conserve : montant total · nombre de versements · prochaine échéance · statut de chaque prélèvement · solde restant.",
          },
        ],
      },
      fin: {
        intro: "Phase 4 — Une carte virtuelle prépayée Visa ou Mastercard permettra d'utiliser un solde EcoWallet admissible auprès de commerçants externes. La carte pourra également être ajoutée à Apple Wallet, Google Wallet ou Samsung Wallet.",
        models: [
          {
            code: "F1",
            title: "EcoWallet fermé · paiements dans l'écosystème EcoLunch",
            roles: {
              commandeur: "Titulaire du compte",
              payeur: "Titulaire du compte",
              beneficiaire: "Bénéficiaires EcoLunch",
              etablissement: "Écosystème EcoLunch",
              fournisseur: "Traiteurs · écoles · garderies · camps · clubs",
              wallet: "EcoWallet (interne)",
              chargement: "Tous rails de pay-in supportés",
              payout: "Payable interne au bénéficiaire choisi",
            },
            flow: "Payeur alimente EcoWallet → Solde disponible dans l'écosystème EcoLunch → Achat dans EcoLunch, EcoKiddo ou EcoSchool → EcoWallet débité → Transaction inscrite au ledger EcoLunch",
            principle: "Le solde est utilisable uniquement dans l'écosystème EcoLunch (repas, frais scolaires, garde, camps, clubs).",
          },
          {
            code: "F2",
            title: "Carte virtuelle prépayée Visa ou Mastercard",
            roles: {
              commandeur: "Titulaire du compte",
              payeur: "EcoWallet (provision)",
              beneficiaire: "Titulaire (utilisation externe)",
              etablissement: "Réseau de cartes (Visa / Mastercard)",
              fournisseur: "Commerçant externe",
              wallet: "EcoWallet (source)",
              chargement: "Provision · solde disponible sur carte",
              payout: "Commerçant payé par le réseau",
            },
            flow: "Solde EcoWallet admissible → Montant disponible sur la carte virtuelle EcoCard → Achat auprès d'un commerçant externe → Autorisation Visa ou Mastercard → Débit du solde associé à EcoWallet → Transaction inscrite au ledger",
            principle: "Le commerçant externe n'est PAS un fournisseur EcoLunch dans le ledger opérationnel.",
          },
          {
            code: "F3",
            title: "Carte EcoCard dans Apple Wallet, Google Wallet ou Samsung Wallet",
            roles: {
              commandeur: "Titulaire du compte",
              payeur: "EcoWallet (autorisation)",
              beneficiaire: "Titulaire",
              etablissement: "Apple Pay / Google Pay / Samsung Pay",
              fournisseur: "Commerçant externe",
              wallet: "EcoWallet (source)",
              chargement: "Tokenisation de la carte virtuelle",
              payout: "Réseau de cartes",
            },
            flow: "Carte virtuelle EcoCard ajoutée à Apple Wallet, Google Wallet ou Samsung Wallet → Carte tokenisée → Paiement auprès d'un commerçant externe → Autorisation du réseau de cartes → Débit du solde associé à EcoWallet → Transaction inscrite au ledger",
            principle: "Une seule carte virtuelle peut être ajoutée à plusieurs wallets natifs.",
          },
          {
            code: "F4",
            title: "Remboursement d'un achat effectué à l'extérieur d'EcoLunch",
            roles: {
              commandeur: "Commerçant externe (initiateur)",
              payeur: "Réseau de cartes",
              beneficiaire: "Titulaire du compte",
              etablissement: "Réseau de cartes",
              fournisseur: "EcoWallet (réception)",
              wallet: "EcoWallet (destination)",
              chargement: "Crédit retour lié à la tx originale",
              payout: "Retour dans le solde EcoWallet",
            },
            flow: "Commerçant externe initie le remboursement → Réseau Visa ou Mastercard transmet le crédit → Carte EcoCard créditée → Montant retourné au solde associé à EcoWallet → Remboursement inscrit au ledger",
            principle: "La transaction de retour reste liée à la transaction d'achat originale (chain id préservée).",
          },
        ],
      },
    },
    globalFlow: {
      title: "Flow central · une seule logique pour tous les modèles",
      subtitle: "Quelle que soit la verticale ou le modèle, la séquence comptable est identique",
      bubbles: {
        parents: "Parents",
        daycares: "Garderies",
        camps: "Camps",
        sponsors: "Sponsors",
        caterers: "Traiteurs",
        schools: "Écoles",
        daycaresOut: "Garderies",
        campsClubs: "Camps · Clubs",
      },
      steps: [
        "Création de la commande, inscription ou facture",
        "Identification du commandeur",
        "Identification du payeur réel",
        "Sélection du wallet à financer",
        "Pay-in vers EcoLunch",
        "Chargement comptable du wallet",
        "Débit du wallet pour le service",
        "Classification par verticale et bénéficiaire",
        "Création du payable dans le ledger",
        "Livraison ou confirmation du service",
        "Déduction frais · crédits · remboursements · chargebacks · retenues · réserves",
        "Payout net au traiteur · école · garderie · camp · club",
      ],
      principleTag: "Principe obligatoire",
      principleTitle: "100% du pay-in est reçu par EcoLunch · zéro split au moment du paiement",
      principleBody: "Le ledger EcoLunch conserve le solde de chaque wallet · rattache la provenance de chaque dollar · applique le solde à une commande ou une facture · crée séparément les payables · déclenche les payouts après réconciliation.",
    },
    walletModels: {
      title: "Modèles universels de chargement du wallet",
      subtitle: "Ces 10 modèles W0→W9 sont réutilisables dans TOUTES les verticales",
      headers: ["Code", "Modèle de load-up", "Description"],
      rows: [
        { code: "W0", title: "Solde existant", desc: "Aucun nouveau pay-in · le solde disponible est utilisé" },
        { code: "W1", title: "Load-up exact invisible", desc: "EcoLunch charge exactement le montant nécessaire au checkout" },
        { code: "W2", title: "Top-up manuel", desc: "Le titulaire ajoute un montant dans son wallet à l'avance" },
        { code: "W3", title: "Auto-load programmé · date fixe", desc: "Chargement automatique déclenché à une date précise ou à une échéance calendrier (one-shot ou récurrence par dates)" },
        { code: "W4", title: "Auto-load selon facture", desc: "Le montant exact de la facture est chargé automatiquement" },
        { code: "W5", title: "Auto-load par seuil de solde", desc: "Top-up automatique déclenché quand le solde du wallet tombe sous un seuil prédéfini (déclencheur = montant, pas calendrier)" },
        { code: "W6", title: "Chargement partagé", desc: "Plusieurs payeurs financent une même dépense ou un même enfant" },
        { code: "W7", title: "Crédit ou subvention", desc: "EcoCrédit · crédit institutionnel · sponsor · subvention" },
        { code: "W8", title: "Retour ou remboursement", desc: "Montant retourné dans le wallet après annulation ou ajustement" },
        { code: "W9", title: "Chargement de carte externe", desc: "Solde destiné à la carte virtuelle prépayée (Phase 4)" },
      ],
    },
    rails: {
      title: "Rails de financement disponibles",
      subtitle: "Chaque modèle de load-up identifie son rail · cumulatifs et combinables",
      items: [
        "Visa", "Mastercard", "Apple Pay", "Google Pay", "Samsung Pay", "Interac",
        "PAD (prélèvement bancaire autorisé)", "Lien de paiement", "Sezzle",
        "EcoCash / EcoCrédits", "Crédit institutionnel / subvention", "Coparent / tiers payeur",
      ],
    },
    ledger: {
      title: "Champs ledger · une seule source de vérité",
      subtitle: "Chaque mouvement conserve l'intégralité de ces métadonnées pour audit et payouts",
      fields: [
        "vertical",
        "transaction_model",
        "ordered_by",
        "paid_by",
        "wallet_owner",
        "funding_source",
        "funding_rail",
        "child_or_service_beneficiary",
        "economic_beneficiary",
        "payable_recipient",
        "fee_rule",
        "contract_version",
        "original_transaction_id",
        "refund_or_chargeback_status",
      ],
    },
    why: {
      title: "Pourquoi Merchant of Record",
      subtitle: "Quatre raisons fondamentales pour lesquelles le split-payment au pay-in est incompatible",
      points: [
        { title: "Le parent ne paie pas le traiteur directement", desc: "Le parent charge EcoLunch. La valeur devient ensuite un payable conditionnel au fournisseur, jamais un paiement direct." },
        { title: "Le montant final n'est connu qu'après livraison", desc: "Annulations, crédits, ajustements, frais et chargebacks changent les payables — impossible à calculer au pay-in." },
        { title: "Les crédits restent dans l'écosystème", desc: "Un crédit peut être réutilisé pour un autre enfant, traiteur, école, camp ou club — sans mouvement bancaire externe." },
        { title: "Une seule source de vérité", desc: "Le ledger EcoLunch enregistre tous les top-ups, soldes, allocations, frais et payouts. Le processeur exécute, ne décide pas." },
      ],
    },
    footer: "EcoLunch — Architecture Merchant of Record · 4 verticales · 1 wallet · 1 ledger",
  },

  /* ============================================================
     ENGLISH
     ============================================================ */
  en: {
    nav: {
      brand: "EcoLunch",
      hero: "Overview",
      roadmap: "Roadmap",
      verticals: "Verticals",
      flow: "Global flow",
      wallet: "Wallet · W0→W9",
      ledger: "Ledger",
      why: "Why MoR",
    },
    hero: {
      title: "One infrastructure.\nFour verticals.\nOne wallet.",
      subtitle:
        "Unified Merchant of Record architecture — internal wallet, PostgreSQL ledger and weekly payouts. One source of truth for meals, education, daycare, sports and financial services.",
      pills: [
        "Merchant of Record",
        "EcoWallet · closed-loop",
        "PostgreSQL ledger",
        "Weekly payouts",
        "Zero split at pay-in",
      ],
    },
    payoutCycle: {
      tag: "Payout cycle · many-to-one accumulation",
      title: "Multiple payable entries → one single outbound batch",
      subtitle: "Each customer transaction creates an entry in the provider's sub-ledger. Payouts are NEVER directly linked to a customer payment — they emerge from an aggregated cycle.",
      payee: "Sub-ledger · Caterer ABC",
      cycle: "Weekly cycle · Friday 8:00 AM",
      railsHeader: "Rail",
      entries: [
        { id: "TX-001", label: "R1 order 10021 · Parent A",       rail: "Visa",    amt: "+ $42.50", kind: "in" },
        { id: "TX-014", label: "R2 institutional · CSS-A",        rail: "PAD",     amt: "+ $38.00", kind: "in" },
        { id: "TX-022", label: "R5 subsidized · School B",        rail: "PAD",     amt: "+ $28.75", kind: "in" },
        { id: "TX-031", label: "R3 optional camp · Parent B",     rail: "Interac", amt: "+ $15.00", kind: "in" },
        { id: "ADJ-01", label: "Refund TX-018",                   rail: "—",       amt: "− $12.00", kind: "out" },
        { id: "ADJ-02", label: "Cyclical reserve",                rail: "—",       amt: "− $10.00", kind: "out" },
      ],
      net: "Batch net",
      netAmt: "= $102.25",
      batchId: "BATCH-2026-24",
      newTx: "New outbound transaction",
      target: "Caterer ABC",
      legend: "Multiple customer payments ≠ multiple payouts. The output is ONE aggregated instruction, distinct from the original pay-in.",
      feeNote: "Rail fees (Visa, Mastercard, Interac, PAD, Sezzle, etc.) are deducted AT PAY-IN based on the mode used for EACH transaction. The amounts above are already net of their respective rail fees — no global % fee is applied to the cycle's GMV.",
    },
    megaFlow: {
      navLabel: "MoR architecture",
      tag: "Overview · multi-vertical · many-to-one",
      title: "The complete architecture · 5 verticals converge into 1 single MoR",
      subtitle: "No split at pay-in. Every customer transaction follows the same convergent path to the central ledger. Payouts are born elsewhere, later, in an aggregated cycle.",
      columns: {
        sources: "5 pay-in sources",
        mid: "1 unique MID",
        ledger: "1 central ledger",
        adjust: "Adjustments",
        sub: "Sub-ledgers",
        batch: "Batching cycle",
        out: "Outbound payout",
      },
      sources: [
        { id: "ec-lunch",  label: "EcoLunch",  desc: "Parents · meals",          color: "#fb923c", phase: "Phase 1" },
        { id: "ec-kiddo",  label: "EcoKiddo",  desc: "Parents · care · camps",   color: "#fbbf24", phase: "Phase 2" },
        { id: "ec-school", label: "EcoSchool", desc: "Parents · school fees",    color: "#22d3ee", phase: "Phase 3" },
        { id: "ec-card",   label: "EcoCard",   desc: "Holders · prepaid",        color: "#3b82f6", phase: "Phase 4" },
        { id: "ec-city",   label: "EcoCity",   desc: "Residents · municipal",    color: "#10b981", phase: "Phase 5" },
      ],
      mid: { label: "EcoTech MID", desc: "Single Merchant ID · all verticals" },
      ledger: { label: "EcoLunch Ledger", desc: "Single source of truth · PostgreSQL" },
      adjustments: [
        "Refunds", "Cancellations", "Customer credits", "Contractual fees", "Cyclical reserves", "Chargebacks",
      ],
      subledgers: [
        { id: "sl-trait",  label: "Caterer · sub-ledger" },
        { id: "sl-gard",   label: "Daycare · sub-ledger" },
        { id: "sl-camp",   label: "Camp / Club · sub-ledger" },
        { id: "sl-school", label: "School / board · sub-ledger" },
        { id: "sl-city",   label: "City / Municipal · sub-ledger" },
      ],
      batch: { label: "Periodic cycle", desc: "Weekly or monthly" },
      payout: { label: "Net payout", desc: "1 outbound instruction per cycle · per recipient" },
      keyFacts: [
        "All verticals converge into ONE single MID (EcoTech). No split at pay-in.",
        "The customer pay-in and the provider payout are TWO distinct financial transactions.",
        "The central ledger is the only source of truth — it computes the contractual payable after delivery.",
        "Outbound payouts are N→1: multiple sub-ledger entries aggregated into ONE payment instruction.",
        "EcoCash / EcoCredits stay inside the ecosystem — no external bank movement.",
      ],
    },
    commonRule: {
      tag: "Common transactional rule",
      body: "The payer pays EcoTechSystem inc. — DBA EcoLunch. The pay-in is processed under the EcoTech MID and the funds are settled to EcoTech. The EcoLunch ledger records every transaction, applies the contractual rules and determines the payable amounts. Payouts to caterers, schools, boards, daycares, camps or clubs are distinct from the customer's initial payment.",
      sequenceTag: "Uniform financial architecture",
      sequenceGroups: [
        {
          tag: "Customer transaction",
          steps: [
            "Customer payment to EcoTech / EcoLunch",
            "Pay-in processed under the EcoTech MID",
            "100% of the funds settled to EcoTech",
            "EcoWallet funded for the exact amount required, or existing EcoWallet balance used",
            "EcoWallet debited for the purchase",
            "Customer transaction, source of funds and service allocation recorded in the EcoTech ledger",
            "Provider allocation recorded as contingent and non-payable",
          ],
        },
        {
          tag: "Contractual payable",
          steps: [
            "Service delivered or applicable contractual condition confirmed",
            "EcoTech ledger calculates and creates the contractual net payable",
          ],
        },
        {
          tag: "Separate payout transaction",
          steps: [
            "EcoTech creates a new and independent payout instruction",
            "Payout executed from EcoTech's payout account through the applicable payout rail",
            "Payout delivered to the contractual payee and reconciled in the EcoTech ledger",
          ],
        },
      ],
    },
    roadmap: {
      title: "Deployment roadmap",
      subtitle: "Five phases · one single MoR infrastructure",
      phases: [
        {
          tag: "Phase 1",
          title: "EcoLunch",
          desc: "School, daycare and camp meals · initial caterer cohort",
          color: "#fb923c",
          mccs: ["5811"],
        },
        {
          tag: "Phase 2",
          title: "EcoKiddo / EcoCamp / EcoClub",
          desc: "Daycares · camps · clubs · sports · leisure · subscriptions",
          color: "#fbbf24",
          mccs: ["8351", "7032", "7033", "7997", "7999", "7911"],
        },
        {
          tag: "Phase 3",
          title: "EcoSchool",
          desc: "School supplies · childcare fees · activities · field trips · materials · school fees",
          color: "#22d3ee",
          mccs: ["8211"],
        },
        {
          tag: "Phase 4",
          title: "EcoCard",
          desc: "Virtual prepaid Visa or Mastercard · external payments · Apple Pay · Google Pay · Samsung Pay",
          color: "#3b82f6",
          mccs: ["6051"],
        },
        {
          tag: "Phase 5",
          title: "EcoCity",
          desc: "Municipal services · citizens · public leisure · city activities · eligible taxes & permits",
          color: "#10b981",
          mccs: ["9399"],
        },
      ],
    },
    verticals: {
      title: "Four verticals · sorted by economic role",
      subtitle: "Pick a tab to see the models, payers, beneficiaries and animated diagram",
      labels: {
        commandeur: "Orderer",
        payeur: "Payer",
        beneficiaire: "Service beneficiary",
        etablissement: "Venue / context",
        fournisseur: "Payable provider",
        wallet: "Funded wallet",
        chargement: "Load-up type",
        payout: "Payout",
        flow: "Flow",
        viewAnimation: "View animation",
        principle: "Note",
        mccList: "MCCs accepted for this vertical",
      },
      tabs: [
        { id: "resto",  emoji: "🍱", tag: "EcoLunch",  short: "EcoLunch",  mcc: "5811", eco: "EcoLunch",  long: "Meals for children · schools · daycares · camps · clubs",
          mccs: [
            { code: "5811", label: "Eating Places · Caterers" },
          ],
        },
        { id: "kiddo",  emoji: "👶", tag: "EcoKiddo",  short: "EcoKiddo",  mcc: "8351", eco: "EcoKiddo",  long: "Daycares · camps · clubs · sports · leisure · subscriptions",
          mccs: [
            { code: "8351", label: "Child Care Services" },
            { code: "7032", label: "Sporting & Recreational Camps" },
            { code: "7033", label: "Campgrounds (campsite-only)" },
            { code: "7997", label: "Sports / Recreation Clubs" },
            { code: "7999", label: "Recreation Services" },
            { code: "7911", label: "Dance Schools / Studios" },
            { code: "9399", label: "Municipal Services" },
          ],
        },
        { id: "school", emoji: "🎓", tag: "EcoSchool", short: "EcoSchool", mcc: "8211", eco: "EcoSchool", long: "School supplies, school childcare fees, activities, field trips, educational materials, administrative charges, and other school fees paid by parents.",
          mccs: [
            { code: "8211", label: "Schools · Elementary & Secondary" },
          ],
        },
        { id: "fin",    emoji: "💳", tag: "EcoCard",   short: "EcoCard",   mcc: "6051", eco: "EcoCard",   long: "Virtual prepaid Visa or Mastercard linked to an eligible EcoWallet balance, usable at external merchants and compatible with Apple Pay, Google Pay and Samsung Pay (Phase 4).",
          mccs: [
            { code: "6051", label: "Quasi-Cash · Prepaid Card Load" },
          ],
        },
      ],
      resto: {
        intro: "EcoLunch centralizes meal orders and payments for children. Depending on the scenario, the parent or the institution orders and pays for the meals. The caterer prepares and delivers the meals, then receives the contractually payable amount based on the EcoLunch ledger data.",
        models: [
          {
            code: "R1",
            title: "School meals · the parent orders and pays for their child",
            roles: {
              commandeur: "Parent",
              payeur: "Parent or co-parents",
              beneficiaire: "Child",
              etablissement: "School",
              fournisseur: "Caterer",
              wallet: "Family EcoLunch wallet",
              chargement: "Exact amount at order · balance · EcoCredits",
              payout: "Caterer (after delivery + reconciliation)",
            },
            flow: "Parent selects meals for their child → Payment to EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Transaction recorded in the EcoLunch ledger → Meal delivered → Delivery confirmed → Caterer net payable computed → Entry added to the caterer sub-ledger",
            principle: "The ledger keeps two states: (1) funds received but non-payable until delivery is confirmed, (2) net payable computed after confirmation (contractual price − cancellations · refunds · chargebacks · reserves · fees). The school only provides operational context.",
          },
          {
            code: "R2",
            title: "Institutional order · orderer and payer distinct",
            roles: {
              commandeur: "Internal manager (school · daycare · camp)",
              payeur: "Institutional account (PAD auto-debited)",
              beneficiaire: "Designated children",
              etablissement: "School · daycare · camp",
              fournisseur: "Caterer",
              wallet: "B2B pay-in → EcoLunch internal wallet",
              chargement: "PAD · auto-debit on institutional account",
              payout: "Caterer (after delivery + reconciliation)",
            },
            flow: "Institution creates the order → Designated institutional payer pays EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Transaction recorded in the EcoLunch ledger → Meals delivered → Delivery confirmed → Caterer net payable computed → Entry added to the caterer sub-ledger",
            principle: "Parent is NOT in this ecosystem. Orderer is not the payer — debit is automatic on the institutional account.",
          },
          {
            code: "R3",
            title: "Camp or club · choice between brought lunch and caterer meal",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Camp or club",
              fournisseur: "Caterer (only for ordered meals)",
              wallet: "Family wallet",
              chargement: "Per-date selection · exact amount",
              payout: "Caterer",
            },
            flow: "Camp or club publishes the meal option → Parent picks a brought lunch or a caterer meal → Brought lunch: no meal transaction → Caterer meal: payment to EcoLunch → Transaction recorded in the EcoLunch ledger → Meal delivered → Delivery confirmed → Caterer net payable computed → Entry added to the caterer sub-ledger",
            principle: "Parent may choose caterer meal OR home lunch per day.",
          },
          {
            code: "R4",
            title: "Camp or club · meals provided to everyone · institutional model",
            roles: {
              commandeur: "Camp or club",
              payeur: "Camp or contractually designated payer",
              beneficiaire: "All enrolled children",
              etablissement: "Camp or club",
              fournisseur: "Caterer",
              wallet: "Institutional wallet",
              chargement: "Institutional global load-up",
              payout: "Caterer",
            },
            flow: "Camp or club confirms the number of participants → Institutional meal order → Camp or club pays EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Transaction recorded in the EcoLunch ledger → Meals delivered to the institution → Delivery confirmed → Caterer net payable computed → Entry added to the caterer sub-ledger",
            principle: "Camp is an active payer. Meals are bundled in the camp registration fee.",
          },
          {
            code: "R5",
            title: "Subsidized meals · the parent orders · the school is billed monthly",
            roles: {
              commandeur: "Parent (daily orders)",
              payeur: "School or board (consolidated monthly billing)",
              beneficiaire: "Designated children",
              etablissement: "School",
              fournisseur: "Caterer",
              wallet: "B2B school pay-in → EcoLunch internal wallet",
              chargement: "Monthly cumulation of parent orders · EcoLunch month-end invoice",
              payout: "Caterer · added to regular payout",
            },
            flow: "Parent orders the subsidized meal for their child → No payment required from the parent → Meal recorded in the ledger as subsidized → Meal delivered → Delivery confirmed → Amount added to the school or board monthly billing → School or board pays EcoLunch → Payment reconciled in the ledger → Caterer net payable computed per the contractual rules → Entry added to the caterer sub-ledger",
            principle: "Parents order without paying. The school or board receives a consolidated monthly invoice and settles EcoLunch. The caterer absorbs contractual fees.",
          },
        ],
      },
      kiddo: {
        intro: "EcoKiddo centralizes registrations, childcare fees, camps, clubs, sports activities, leisure and subscriptions. Fees may be paid by a parent, co-parent or authorized third party. Each payment is received by EcoTech, recorded in the EcoLunch ledger and used to compute the amount payable to the concerned organization.",
        models: [
          {
            code: "G1",
            title: "Daycare · registration and one-off fees",
            roles: {
              commandeur: "Daycare",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Daycare / CPE",
              fournisseur: "Daycare",
              wallet: "Family wallet",
              chargement: "On-demand single pay-in",
              payout: "Daycare",
            },
            flow: "Daycare creates the registration or one-off fee → Parent receives the request in EcoKiddo → Parent pays EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Payment recorded in the EcoLunch ledger → Registration or fee confirmed → Daycare net payable computed → Entry added to the daycare sub-ledger",
            principle: "Examples: registration · deposit · admin fee · extra day · late fee · special activity.",
          },
          {
            code: "G2",
            title: "Daycare · recurring childcare fees",
            roles: {
              commandeur: "Daycare",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Daycare",
              fournisseur: "Daycare",
              wallet: "Family wallet",
              chargement: "Periodic auto-load (weekly · bi-weekly · monthly · custom)",
              payout: "Daycare",
            },
            flow: "Care schedule established → Periodic invoice created → Automatic payment authorized → EcoWallet topped up to the required amount → Payment to EcoLunch on the due date → Transaction recorded in the EcoLunch ledger → Daycare net payable computed → Entry added to the daycare sub-ledger",
            principle: "Amount set per weekly · bi-weekly · monthly or custom calendar.",
          },
          {
            code: "G3",
            title: "Daycare · fees adjusted for attendance and absences",
            roles: {
              commandeur: "Daycare (billing engine)",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Daycare",
              fournisseur: "Daycare",
              wallet: "Family wallet",
              chargement: "Final amount after attendance validation",
              payout: "Daycare",
            },
            flow: "Attendance and absences validated → Final amount computed → Adjusted invoice → Parent pays EcoLunch → Transaction recorded in the EcoLunch ledger → Daycare net payable computed → Entry added to the daycare sub-ledger",
            principle: "Engine decides if an absence is payable, creditable, refundable or excluded from next bill.",
          },
          {
            code: "G4",
            title: "Daycare · fees split across several payers",
            roles: {
              commandeur: "Daycare",
              payeur: "Co-parent or third-party payer",
              beneficiaire: "Child",
              etablissement: "Daycare",
              fournisseur: "Daycare",
              wallet: "Designated payer's wallet (split possible)",
              chargement: "Split across payers · parallel loads",
              payout: "Daycare (consolidated)",
            },
            flow: "Childcare fees assigned → Split across authorized payers → Each payer pays their share to EcoLunch → Payments consolidated in the EcoLunch ledger → Total amount paid validated → Daycare net payable computed → Entry added to the daycare sub-ledger",
            principle: "Typical case: 50/50 co-parenting or third-party payer (grandparent, sponsor, employer).",
          },
          {
            code: "L1",
            title: "Camp or club · registration paid in full",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Family wallet",
              chargement: "Single pay-in at registration",
              payout: "Camp / club",
            },
            flow: "Camp or club publishes the activity → Parent registers their child → Parent pays the full amount to EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Payment recorded in the EcoLunch ledger → Registration confirmed → Camp or club net payable computed → Entry added to the camp or club sub-ledger",
            principle: "Simplest model: one payment at registration.",
          },
          {
            code: "L2",
            title: "Camp or club · registration paid in installments",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Family wallet",
              chargement: "Initial deposit · then scheduled auto-loads until balance is paid",
              payout: "Camp / club (staggered or consolidated)",
            },
            flow: "Registration confirmed → Initial deposit paid to EcoLunch → Schedule activated → Installments paid to EcoLunch on scheduled dates → Each payment is recorded in the EcoLunch ledger → Camp or club net payable computed per the contractual schedule → Entry added to the camp or club sub-ledger",
            principle: "Total amount is known and finite (e.g. $1,200 in 4 installments). Ledger keeps schedule · remaining balance · status per installment. Distinct from L3 (open-ended subscription).",
          },
          {
            code: "L3",
            title: "Club · subscription and recurring fees",
            roles: {
              commandeur: "Club",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Club",
              fournisseur: "Club",
              wallet: "Family wallet",
              chargement: "Indefinite recurring auto-load (monthly · quarterly · yearly)",
              payout: "Club",
            },
            flow: "Parent subscribes to the membership → Billing calendar activated → Recurring payment to EcoLunch at each due date → Transaction recorded in the EcoLunch ledger → Club net payable computed → Entry added to the club sub-ledger",
            principle: "SaaS-style subscription · no capped total · indefinite recurrence. Distinct from L2 (finite-total registration with schedule).",
          },
          {
            code: "L4",
            title: "Camp or club · add-ons and additional fees",
            roles: {
              commandeur: "Parent",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Camp / club",
              fournisseur: "Camp / club",
              wallet: "Family wallet",
              chargement: "One-off complementary invoice",
              payout: "Camp / club",
            },
            flow: "Parent adds an option or service → Additional fee computed → Parent pays EcoLunch → Payment recorded in the EcoLunch ledger → Option confirmed → Camp or club net payable computed → Entry added to the camp or club sub-ledger",
            principle: "Examples: trips · transport · equipment · extended care · special activities.",
          },
          {
            code: "L5",
            title: "Camp · registration and caterer meals in a single cart",
            roles: {
              commandeur: "Parent (registration) + Camp (meals)",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "Camp",
              fournisseur: "Camp (registration) + Caterer (meals)",
              wallet: "Family wallet",
              chargement: "Single multi-vertical payment",
              payout: "Camp AND Caterer (separated in ledger)",
            },
            flow: "Parent selects the registration and meals → Single payment to EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → EcoLunch ledger separates the registration portion and the meal portion → Registration portion: camp net payable computed per registration rules → Meal portion: non-payable amount until delivery → Meal delivered → Delivery confirmed → Caterer net payable computed → Entries added to camp AND caterer sub-ledgers (separately)",
            principle: "No split at the PSP. Separation strictly performed inside the EcoLunch ledger.",
          },
        ],
      },
      school: {
        intro: "Schools and boards create school fees in EcoSchool. Parents view and pay these fees in the « School fees » section of the EcoLunch app. Payments are received by EcoTech, recorded in the EcoLunch ledger and lead to an amount payable to the school or board per the applicable rules.",
        models: [
          {
            code: "S1",
            title: "School fees · single payment by the parent",
            roles: {
              commandeur: "School or board (biller)",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "School / board",
              fournisseur: "School, board or designated organization",
              wallet: "Family wallet",
              chargement: "Payment request · single pay-in",
              payout: "School / board / designated organization",
            },
            flow: "School or board creates the fee → Parent receives the request in the EcoLunch app → Parent pays EcoLunch → Pay-in under the EcoTech MID → Funds settled to EcoTech → Payment recorded in the EcoLunch ledger → School or board net payable computed per the fee rules → Entry added to the school or board sub-ledger",
            principle: "Examples: activities, trips, supplies, administrative fees or school services.",
          },
          {
            code: "S2",
            title: "School fees · payment in scheduled installments",
            roles: {
              commandeur: "School (creates schedule)",
              payeur: "Parent",
              beneficiaire: "Child",
              etablissement: "School / board",
              fournisseur: "School / board",
              wallet: "Family wallet",
              chargement: "Scheduled auto-load · per installment",
              payout: "Institution",
            },
            flow: "School or board creates the fee and the schedule → Parent authorizes the installments → EcoWallet topped up to the required amount on each due date → Installment paid to EcoLunch → Payment recorded in the EcoLunch ledger → School or board net payable computed for each amount paid → Entry added to the school or board sub-ledger",
            principle: "Ledger keeps: total · installment count · next due date · per-debit status · remaining balance.",
          },
        ],
      },
      fin: {
        intro: "Phase 4 — A prepaid virtual Visa or Mastercard will let users spend an eligible EcoWallet balance at external merchants. The card can also be added to Apple Wallet, Google Wallet or Samsung Wallet.",
        models: [
          {
            code: "F1",
            title: "Closed EcoWallet · payments inside the EcoLunch ecosystem",
            roles: {
              commandeur: "Account holder",
              payeur: "Account holder",
              beneficiaire: "EcoLunch beneficiaries",
              etablissement: "EcoLunch ecosystem",
              fournisseur: "Caterers · schools · daycares · camps · clubs",
              wallet: "EcoWallet (internal)",
              chargement: "All supported pay-in rails",
              payout: "Internal payable to chosen beneficiary",
            },
            flow: "Payer tops up EcoWallet → Balance available inside the EcoLunch ecosystem → Purchase in EcoLunch, EcoKiddo or EcoSchool → EcoWallet debited → Transaction recorded in the EcoLunch ledger",
            principle: "Balance usable only inside the EcoLunch ecosystem (meals, school fees, childcare, camps, clubs).",
          },
          {
            code: "F2",
            title: "Prepaid virtual Visa or Mastercard card",
            roles: {
              commandeur: "Account holder",
              payeur: "EcoWallet (provisioning)",
              beneficiaire: "Holder (external use)",
              etablissement: "Card network (Visa / Mastercard)",
              fournisseur: "External merchant",
              wallet: "EcoWallet (source)",
              chargement: "Provisioning · balance available on card",
              payout: "Merchant paid by the network",
            },
            flow: "Eligible EcoWallet balance → Amount available on the EcoCard virtual card → Purchase at an external merchant → Visa or Mastercard authorization → Debit on the balance linked to EcoWallet → Transaction recorded in the ledger",
            principle: "The external merchant is NOT an EcoLunch supplier in the operational ledger.",
          },
          {
            code: "F3",
            title: "EcoCard in Apple Wallet, Google Wallet or Samsung Wallet",
            roles: {
              commandeur: "Account holder",
              payeur: "EcoWallet (authorization)",
              beneficiaire: "Holder",
              etablissement: "Apple Pay / Google Pay / Samsung Pay",
              fournisseur: "External merchant",
              wallet: "EcoWallet (source)",
              chargement: "Virtual card tokenization",
              payout: "Card network",
            },
            flow: "EcoCard virtual card added to Apple Wallet, Google Wallet or Samsung Wallet → Card tokenized → Payment at an external merchant → Card network authorization → Debit on the balance linked to EcoWallet → Transaction recorded in the ledger",
            principle: "A single virtual card can be added to multiple native wallets.",
          },
          {
            code: "F4",
            title: "Refund of a purchase made outside EcoLunch",
            roles: {
              commandeur: "External merchant (initiator)",
              payeur: "Card network",
              beneficiaire: "Account holder",
              etablissement: "Card network",
              fournisseur: "EcoWallet (recipient)",
              wallet: "EcoWallet (destination)",
              chargement: "Return credit linked to original tx",
              payout: "Returned to EcoWallet balance",
            },
            flow: "External merchant initiates the refund → Visa or Mastercard network transmits the credit → EcoCard credited → Amount returned to the balance linked to EcoWallet → Refund recorded in the ledger",
            principle: "The return transaction remains linked to the original purchase transaction (chain id preserved).",
          },
        ],
      },
    },
    globalFlow: {
      title: "Central flow · one logic for every model",
      subtitle: "Whichever the vertical or model, the accounting sequence is identical",
      bubbles: {
        parents: "Parents",
        daycares: "Daycares",
        camps: "Camps",
        sponsors: "Sponsors",
        caterers: "Caterers",
        schools: "Schools",
        daycaresOut: "Daycares",
        campsClubs: "Camps · Clubs",
      },
      steps: [
        "Create order, registration or invoice",
        "Identify the orderer",
        "Identify the real payer",
        "Pick the wallet to fund",
        "Pay-in to EcoLunch",
        "Accounting load of the wallet",
        "Debit wallet for the service",
        "Classify by vertical and beneficiary",
        "Create the payable in the ledger",
        "Deliver or confirm the service",
        "Deduct fees · credits · refunds · chargebacks · holds · reserves",
        "Net payout to caterer · school · daycare · camp · club",
      ],
      principleTag: "Mandatory principle",
      principleTitle: "100% of the pay-in is received by EcoLunch · zero split at payment time",
      principleBody: "The EcoLunch ledger keeps each wallet's balance · tracks every dollar's origin · applies balance to orders or invoices · separately creates payables · triggers payouts after reconciliation.",
    },
    walletModels: {
      title: "Universal wallet load-up models",
      subtitle: "These 10 W0→W9 models are reusable across EVERY vertical",
      headers: ["Code", "Load-up model", "Description"],
      rows: [
        { code: "W0", title: "Existing balance", desc: "No new pay-in · the available balance is used" },
        { code: "W1", title: "Invisible exact load-up", desc: "EcoLunch loads exactly the amount needed at checkout" },
        { code: "W2", title: "Manual top-up", desc: "Holder pre-loads an amount into their wallet" },
        { code: "W3", title: "Scheduled auto-load · fixed date", desc: "Automatic load-up triggered on a specific date or calendar due date (one-shot or recurrence by dates)" },
        { code: "W4", title: "Auto-load per invoice", desc: "Exact invoice amount is loaded automatically" },
        { code: "W5", title: "Balance-threshold auto-load", desc: "Automatic top-up triggered when the wallet balance drops below a predefined threshold (trigger = amount, not calendar)" },
        { code: "W6", title: "Shared load-up", desc: "Multiple payers fund the same expense or child" },
        { code: "W7", title: "Credit or subsidy", desc: "EcoCredit · institutional credit · sponsor · subsidy" },
        { code: "W8", title: "Return or refund", desc: "Amount returned to wallet after cancellation or adjustment" },
        { code: "W9", title: "External card load-up", desc: "Balance allocated to the prepaid virtual card (Phase 4)" },
      ],
    },
    rails: {
      title: "Available funding rails",
      subtitle: "Each load-up model identifies its rail · cumulative and combinable",
      items: [
        "Visa", "Mastercard", "Apple Pay", "Google Pay", "Samsung Pay", "Interac",
        "Pre-authorized debit (PAD)", "Payment link", "Sezzle",
        "EcoCash / EcoCredits", "Institutional credit / subsidy", "Co-parent / third-party payer",
      ],
    },
    ledger: {
      title: "Ledger fields · single source of truth",
      subtitle: "Every movement carries the full metadata set for audit and payouts",
      fields: [
        "vertical",
        "transaction_model",
        "ordered_by",
        "paid_by",
        "wallet_owner",
        "funding_source",
        "funding_rail",
        "child_or_service_beneficiary",
        "economic_beneficiary",
        "payable_recipient",
        "fee_rule",
        "contract_version",
        "original_transaction_id",
        "refund_or_chargeback_status",
      ],
    },
    why: {
      title: "Why Merchant of Record",
      subtitle: "Four fundamental reasons why split-payment at pay-in is incompatible",
      points: [
        { title: "Parents don't pay caterers directly", desc: "The parent funds EcoLunch. The value then becomes a conditional payable to a supplier — never a direct payment." },
        { title: "Final amount is only known after delivery", desc: "Cancellations, credits, adjustments, fees and chargebacks change payables — impossible to compute at pay-in." },
        { title: "Credits stay in the ecosystem", desc: "A credit can be reused for another child, caterer, school, camp or club — without any external banking movement." },
        { title: "Single source of truth", desc: "The EcoLunch ledger records every top-up, balance, allocation, fee and payout. The processor executes, never decides." },
      ],
    },
    footer: "EcoLunch — Merchant of Record architecture · 4 verticals · 1 wallet · 1 ledger",
  },
};

export { W_KEYS };
